import type { LegendListRef } from '@legendapp/list/react-native';
import SearchBar from '-/Custom/SearchBar';
import { Stack } from 'expo-router';
import { useRef } from 'react';
import { useQuery, useSubscription } from 'urql';
import Paginator from '@/components/Custom/Paginator';
import * as Placeholder from '@/components/Custom/Placeholder';
import Box from '@/components/UI/Box';
import List, { type ListProps } from '@/components/Unistyles/List';
import { graphql, type ResultOf } from '@/gql/graphql';
import type { BadgeAssigneeBoolExp } from '@/gql/scalars';
import useDebounce from '@/hooks/useDebounce';
import useHeaderBackButton from '@/hooks/useHeaderBackButton';
import usePaginatedList from '@/hooks/usePaginatedList';
import { DEFAULT_SEARCH_MINIMUM } from '@/utils/constants';
import { BadgeAssigneeListItemFragment } from './BadgeAssigneeListItem';

const BADGE_ASSIGNEE_LIMIT = 25;
const EMPTY_BADGE_ASSIGNEE_WHERE: BadgeAssigneeBoolExp = {};

const GetBadgeAssigneeList = graphql(/* GraphQL */ `
  query GetBadgeAssigneeList(
    $badgeAssigneeWhere: badge_assignee_bool_exp = {}
    $badgeAssigneeLimit: Int!
    $badgeAssigneeOffset: Int!
  ) {
    badge_assignee(
      where: $badgeAssigneeWhere
      limit: $badgeAssigneeLimit
      offset: $badgeAssigneeOffset
      order_by: { updated_at: desc }
    ) {
      id
      ...BadgeAssigneeListItem
    }
    badge_assignee_aggregate(where: $badgeAssigneeWhere) {
      aggregate {
        count
      }
    }
  }
`, [BadgeAssigneeListItemFragment]);

type BadgeAssigneeListRow = NonNullable<
  NonNullable<ResultOf<typeof GetBadgeAssigneeList>['badge_assignee']>[number]
>;

const WatchBadgeAssigneeList = graphql(/* GraphQL */ `
  subscription WatchBadgeAssigneeList(
    $badgeAssigneeWhere: badge_assignee_bool_exp = {}
    $badgeAssigneeLimit: Int!
    $badgeAssigneeOffset: Int!
  ) {
    badge_assignee(
      where: $badgeAssigneeWhere
      limit: $badgeAssigneeLimit
      offset: $badgeAssigneeOffset
      order_by: { updated_at: desc }
    ) {
      id
      ...BadgeAssigneeListItem
    }
  }
`, [BadgeAssigneeListItemFragment]);

export default function BadgeAssigneeList({
  badgeAssigneeWhere = EMPTY_BADGE_ASSIGNEE_WHERE,
  search = true,
  ...props
}: {
  search?: boolean;
  badgeAssigneeWhere?: BadgeAssigneeBoolExp;
} & Omit<ListProps<BadgeAssigneeListRow>, 'data'>) {
  const { value, debouncedValue, onChangeText, reset } = useDebounce();
  const listRef = useRef<LegendListRef>(null);
  const paginated = usePaginatedList({ limit: BADGE_ASSIGNEE_LIMIT, listRef });
  const { limit, offset } = paginated;

  const variables = {
    badgeAssigneeLimit: limit,
    badgeAssigneeOffset: debouncedValue.length ? 0 : offset,
    badgeAssigneeWhere: {
      ...badgeAssigneeWhere,
      ...(debouncedValue.length >= DEFAULT_SEARCH_MINIMUM
        ? { person: { name: { _ilike: `%${debouncedValue}%` } } }
        : {}),
    },
  };

  const [{ data, fetching, error }, refresh] = useQuery({
    query: GetBadgeAssigneeList,
    variables,
  });

  useSubscription({
    query: WatchBadgeAssigneeList,
    variables,
  });

  const badgeAssignees = data?.badge_assignee || [];

  const totalBadgeAssignments =
    data?.badge_assignee_aggregate.aggregate?.count || 0;

  const headerLeft = useHeaderBackButton();

  return (
    <>
      <Stack.Screen options={{ headerLeft }} />
      {search ? (
        <Box px="xl">
          <SearchBar
            value={value}
            onPressCancel={reset}
            onChangeText={onChangeText}
            placeholder="Search Assignments..."
          />
        </Box>
      ) : null}
      <Box flex testID="BadgeAssigneeResults">
        <List
          {...props}
          ref={listRef}
          data={badgeAssignees}
          fetching={fetching}
          error={error}
          onRetry={() => refresh({ requestPolicy: 'network-only' })}
          ListEmptyComponent={
            <Placeholder.Container>
              <Placeholder.Text>No Assignments</Placeholder.Text>
            </Placeholder.Container>
          }
          ListFooterComponent={
            totalBadgeAssignments > paginated.limit ? (
              <Paginator {...paginated} total={totalBadgeAssignments} />
            ) : null
          }
          keyExtractor={(item) => item.id}
        />
      </Box>
    </>
  );
}
